import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  Tag,
  Button,
  message,
  Space,
  Typography,
  Form,
  Input,
  DatePicker,
  Alert,
  Table,
} from "antd";
import { LeaveTypeEnum, LeaveTypeEnumLabel } from "Constants/Enums/LeavePolicyEnum";
import { useNavigate } from "react-router-dom";
import LeaveRequestService from "Services/EmployeeServices/LeaveRequestService";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import moment from "moment";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// Point 16: balances are now tracked in hours (days kept only for compatibility).
// Helpers prefer the hour fields and fall back to day fields for older responses.
const fmtHours = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const balanceText = (policy) => {
  const availH = fmtHours(policy.availableHours);
  if (availH !== null) {
    const totalH = fmtHours(policy.annualEntitlementHours);
    return totalH !== null ? `${availH} / ${totalH} hrs` : `${availH} hrs`;
  }
  return `${policy.availableDays} / ${policy.grantedDays} days`;
};

const usedText = (policy) => {
  const usedH = fmtHours(policy.usedPaidHours);
  if (usedH !== null) {
    return Number(policy.usedPaidHours) > 0 ? `${usedH} hrs used` : null;
  }
  return policy.usedDays > 0 ? `${policy.usedDays} days used` : null;
};

const summaryAvailable = (policies) => {
  const hasHours = policies.some((p) => p.availableHours !== undefined && p.availableHours !== null);
  if (hasHours) {
    const total = policies.reduce((s, p) => s + (Number(p.availableHours) || 0), 0);
    return `${fmtHours(total)} hrs`;
  }
  return `${policies.reduce((s, p) => s + (Number(p.availableDays) || 0), 0)} days`;
};

const summaryUsed = (policies) => {
  const hasHours = policies.some((p) => p.usedPaidHours !== undefined && p.usedPaidHours !== null);
  if (hasHours) {
    const total = policies.reduce((s, p) => s + (Number(p.usedPaidHours) || 0), 0);
    return `${fmtHours(total)} hrs`;
  }
  return `${policies.reduce((s, p) => s + (Number(p.usedDays) || 0), 0)} days`;
};

const LeaveRequest = () => {
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [myRequests, setMyRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  // Point 16: paid/unpaid split returned by backend after a request is created.
  const [lastResult, setLastResult] = useState(null);

  const {
    leavePolicies,
    loading,
    fetchLeaveBalance,
    submitLeaveRequest,
    calculateDays,
  } = LeaveRequestService();

  // Fetch employee's leave balance and my requests on component mount
  useEffect(() => {
    fetchLeaveBalance();
    fetchMyRequests();
  }, []);

  // Fetch my leave requests
  const fetchMyRequests = async () => {
    try {
      setRequestsLoading(true);
      const response = await axiosInstance.get(API.leaveRequest.myRequests);
      setMyRequests(response.data);
    } catch (error) {
      console.error("Error fetching my leave requests:", error);
      message.error("Failed to load your leave requests");
    } finally {
      setRequestsLoading(false);
    }
  };

  // Handle policy selection
  const handlePolicySelect = (policyId) => {
    const policy = leavePolicies.find((p) => p.leavePolicyId === policyId);
    setSelectedPolicy(policy);
    // Set the leave type in the form
    form.setFieldsValue({
      leaveType: policy.leaveType,
    });
  };

  // Handle form submission
  const onFinish = async (values) => {
    if (!selectedPolicy) {
      message.warning("Please select a leave policy");
      return;
    }

    try {
      const payload = {
        leaveType: values.leaveType,
        fromDate: values.dateRange[0].format("YYYY-MM-DD"),
        toDate: values.dateRange[1].format("YYYY-MM-DD"),
        reason: values.reason || "",
      };

      // Point 16: do not block when the request is more than the balance.
      // Backend splits it into paid/unpaid and returns the breakdown.
      const result = await submitLeaveRequest(payload);
      setLastResult(result || null);
      form.resetFields();
      setSelectedPolicy(null);
      // Refresh leave balance and requests to update data
      await fetchLeaveBalance();
      await fetchMyRequests();
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  // Disable past dates
  const disabledDate = (current) => {
    return current && current < moment().startOf("day"); // using moment
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      const days = calculateDays(startDate, endDate);
      form.setFieldsValue({ days: days });
    }
  };

  

  

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Apply for Leave Card - First Row */}
      <Card
        title="Apply for  Leave"
        loading={loading}
        className="card-professional"
        style={{ marginBottom: "24px" }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
          {/* Available Policies Section */}
          <div>
            <Text strong style={{ display: "block", marginBottom: 16, fontSize: "16px" }}>
              Available Leave Policies
            </Text>

            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {leavePolicies.map((policy) => (
                <Card
                  key={policy.leavePolicyId}
                  size="small"
                  style={{
                    border:
                      selectedPolicy?.leavePolicyId === policy.leavePolicyId
                        ? "2px solid #1890ff"
                        : "1px solid #d9d9d9",
                    cursor: "pointer",
                    background:
                      selectedPolicy?.leavePolicyId === policy.leavePolicyId ? "#f0f8ff" : "white",
                  }}
                  onClick={() => handlePolicySelect(policy.leavePolicyId)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ display: "block", marginBottom: 4 }}>
                        {policy.policyName}
                      </Text>
                      <Tag style={{ margin: 0, marginBottom: 8, border: "1px solid #d9d9d9" }}>
                        {LeaveTypeEnumLabel[policy.leaveType] || "Unknown"}
                      </Tag>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Policy Year: {policy.policyYear}
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "14px", fontWeight: "500", color: "#389e0d" }}>
                        {balanceText(policy)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>available</div>
                      {usedText(policy) && (
                        <div style={{ fontSize: "11px", color: "#999" }}>{usedText(policy)}</div>
                      )}
                      {policy.accruedThrough && (
                        <div style={{ fontSize: "11px", color: "#999" }}>
                          accrued through {moment(policy.accruedThrough).format("MMM YYYY")}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </Space>

            {/* Summary */}
            {leavePolicies.length > 0 && (
              <Card size="small" style={{ marginTop: 16, background: "#fafafa" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Total Policies</div>
                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {leavePolicies.length}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Available</div>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#389e0d" }}>
                      {summaryAvailable(leavePolicies)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Used</div>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#fa8c16" }}>
                      {summaryUsed(leavePolicies)}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Application Form */}
          <div>
            <Text strong style={{ display: "block", marginBottom: 16, fontSize: "16px" }}>
              Leave Application Form
            </Text>

            {lastResult && lastResult.totalDays !== undefined && (
              <Card
                size="small"
                style={{ marginBottom: 16, background: "#f6ffed", border: "1px solid #b7eb8f" }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Text strong>Leave request submitted</Text>
                  <Button type="text" size="small" onClick={() => setLastResult(null)}>
                    Dismiss
                  </Button>
                </div>
                <Space wrap size="large" style={{ marginTop: 8 }}>
                  <span style={{ fontSize: 12 }}>
                    Total: <b>{lastResult.totalDays}</b> days
                  </span>
                  <span style={{ fontSize: 12, color: "#389e0d" }}>
                    Paid: <b>{lastResult.paidDays}</b> days
                    {fmtHours(lastResult.paidHours) !== null &&
                      ` (${fmtHours(lastResult.paidHours)} hrs)`}
                  </span>
                  <span style={{ fontSize: 12, color: "#cf1322" }}>
                    Unpaid: <b>{lastResult.unpaidDays}</b> days
                    {fmtHours(lastResult.unpaidHours) !== null &&
                      ` (${fmtHours(lastResult.unpaidHours)} hrs)`}
                  </span>
                </Space>
                {Number(lastResult.unpaidDays) > 0 && (
                  <Alert
                    style={{ marginTop: 12 }}
                    type="warning"
                    showIcon
                    message={`${lastResult.unpaidDays} day(s) will be unpaid. The salary deduction will be calculated during payroll processing.`}
                  />
                )}
              </Card>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              disabled={!selectedPolicy}
            >
              {/* Selected Policy Info */}
              {selectedPolicy && (
                <Card
                  size="small"
                  style={{ marginBottom: 16, background: "#f0f8ff", border: "1px solid #91d5ff" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text strong>{selectedPolicy.policyName}</Text>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {LeaveTypeEnumLabel[selectedPolicy.leaveType]} • {balanceText(selectedPolicy)}{" "}
                        available
                      </div>
                    </div>
                    <Button
                      size="small"
                      type="text"
                      onClick={() => {
                        setSelectedPolicy(null);
                        form.resetFields();
                      }}
                    >
                      Change Policy
                    </Button>
                  </div>
                </Card>
              )}

              {/* Leave Type (hidden but included in form) */}
              <Form.Item name="leaveType" hidden>
                <Input />
              </Form.Item>

              {/* Date Range */}
              <Form.Item
                name="dateRange"
                label="Leave Dates"
                rules={[{ required: true, message: "Please select leave dates" }]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  allowEmpty={[true, true]}
                />
              </Form.Item>

              {/* Calculated Days (estimate only - backend excludes weekends/holidays) */}
              <Form.Item
                name="days"
                label="Number of Days (estimate)"
                initialValue={0}
                extra="Estimate only. Weekends, holidays and the paid vs unpaid split are calculated by the system after you submit."
              >
                <Input disabled placeholder="Will be calculated based on selected dates" />
              </Form.Item>

              {/* Reason */}
              <Form.Item
                name="reason"
                label="Reason for Leave"
                rules={[{ required: true, message: "Please provide a reason for leave" }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Please provide the reason for your leave request..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              {/* Submit Button */}
              <Form.Item>
                <Button
                  className="btn-primary"
                  htmlType="submit"
                  style={{ width: "100%" }}
                  disabled={!selectedPolicy}
                  loading={loading}
                >
                  Submit Leave Request
                </Button>
              </Form.Item>
            </Form>

            {!selectedPolicy && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  background: "#f9f9f9",
                  borderRadius: "6px",
                  border: "1px dashed #d9d9d9",
                }}
              >
                <Text type="secondary">
                  Please select a leave policy from the left to apply for leave
                </Text>
              </div>
            )}

            {selectedPolicy &&
              (selectedPolicy.availableHours !== undefined
                ? Number(selectedPolicy.availableHours) <= 0
                : Number(selectedPolicy.availableDays) <= 0) && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#fffbe6",
                    borderRadius: "6px",
                    border: "1px solid #ffe58f",
                  }}
                >
                  <Text type="warning">
                    You have no paid balance left on {selectedPolicy.policyName}. You can still apply,
                    and the days will be processed as unpaid leave.
                  </Text>
                </div>
              )}
          </div>
        </div>
      </Card>
    </DashboardLayout>
  );
};

export default LeaveRequest;
